import React, { useEffect, useState } from "react";
import api, { extractError } from "@/lib/api";
import { personName } from "@/lib/format";
import { INTERACTION_TYPES, PRIORITIES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DictationTextarea } from "@/components/DictationTextarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, UserPlus, Check, Loader2, Phone, User, ClipboardList, FileText, Contact, Users } from "lucide-react";
import { toast } from "sonner";

// Restituisce la data odierna (locale) in formato YYYY-MM-DD per gli input date
const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const GENERI = ["Femminile", "Maschile", "Altro", "Non specificato"];
const MODALITA = ["Telefono", "WhatsApp", "Email", "SMS"];
const FASCE = ["Mattina", "Pomeriggio", "Sera"];

const NP_EMPTY = {
  nome: "", cognome: "", telefono_principale: "", origine_contatto: "",
  data_nascita: "", genere: "", email: "", codice_fiscale: "",
  indirizzo: "", numero_civico: "", citta: "", provincia: "", cap: "",
  modalita_contatto: "", fascia_oraria: "", referente_familiare: "", note: "",
};

// Form riutilizzabile per registrare un contatto (Modalità Centralino + dialog rapido)
export const ContactRegistration = ({ onDone, defaultPersonId = null, big = false }) => {
  const [services, setServices] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newMode, setNewMode] = useState(false);
  const [np, setNp] = useState({ ...NP_EMPTY });
  const [form, setForm] = useState({
    servizio_id: "", priorita: "Normale", tipo_contatto: "Telefonata ricevuta",
    data_contatto: todayISO(),
    richiesta: "", risposta: "", crea_pratica: true, richiamare: false, data_richiamo: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/services").then(({ data }) => setServices(data.filter((s) => s.attivo))).catch(() => {}); }, []);
  useEffect(() => {
    if (defaultPersonId) api.get(`/persons/${defaultPersonId}`).then(({ data }) => setSelected(data.persona)).catch(() => {});
  }, [defaultPersonId]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/persons?q=${encodeURIComponent(query)}&limit=6`); setResults(data.items); } catch (e) { console.error("Errore ricerca persona", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setP = (k, v) => setNp((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!selected && !(newMode && np.cognome && np.telefono_principale)) {
      toast.error("Seleziona una persona o inserisci una nuova scheda (cognome e telefono)"); return;
    }
    if (!form.servizio_id) { toast.error("Seleziona il servizio"); return; }
    if (!form.richiesta.trim()) { toast.error("Descrivi la situazione in ingresso"); return; }
    if (form.data_contatto && form.data_contatto > todayISO()) {
      toast.error("La data del contatto non può essere futura"); return;
    }
    setSaving(true);
    try {
      // Includi solo i campi anagrafici valorizzati per la nuova persona
      let nuova = null;
      if (!selected && newMode) {
        nuova = Object.fromEntries(Object.entries(np).filter(([, v]) => v !== "" && v != null));
      }
      const payload = {
        person_id: selected?.id || null,
        nuova_persona: nuova,
        servizio_id: form.servizio_id, priorita: form.priorita, tipo_contatto: form.tipo_contatto,
        data_contatto: form.data_contatto || null,
        richiesta: form.richiesta, risposta: form.risposta || null,
        crea_pratica: form.crea_pratica, richiamare: form.richiamare,
        data_richiamo: form.richiamare && form.data_richiamo ? new Date(form.data_richiamo).toISOString() : null,
      };
      const { data } = await api.post("/centralino/register", payload);
      toast.success(data.numero_pratica ? `Salvato. Pratica ${data.numero_pratica} assegnata a ${data.responsabile_nome || "servizio"}.` : "Contatto registrato.");
      onDone && onDone(data);
    } catch (e) {
      toast.error(extractError(e, "Errore durante il salvataggio"));
    } finally { setSaving(false); }
  };

  const sectionCls = "rounded-xl border bg-card";

  return (
    <div className="space-y-4" data-testid="centralino-form">
      {/* 1) Persona: ricerca o nuova scheda */}
      <div className={`${sectionCls} p-4`}>
        <div className="flex items-center gap-2 mb-2">
          <User className="h-4 w-4 text-[color:var(--brand-blue-600)]" />
          <h3 className="font-display font-semibold text-sm">Persona / contatto</h3>
        </div>
        <Label htmlFor="centralino-person-search">Cerca per nome o telefono</Label>
        <div className="relative mt-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input id="centralino-person-search" value={query} onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
            placeholder="Es. Rossi oppure 333..." className="pl-9 h-11" data-testid="centralino-person-search" />
        </div>
        {selected ? (
          <div className="mt-2 rounded-lg border bg-[color:var(--brand-green-100)] p-3 flex items-center justify-between" data-testid="centralino-selected-person">
            <div><div className="font-medium">{personName(selected)}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{selected.telefono_principale || "-"}</div></div>
            <button type="button" onClick={() => setSelected(null)} className="text-xs text-[color:var(--brand-blue-700)] hover:underline">Cambia</button>
          </div>
        ) : (
          <>
            {results.length > 0 && (
              <div className="mt-2 space-y-1 max-h-56 overflow-auto ras-scroll">
                {results.map((p) => (
                  <button key={p.id} type="button" onClick={() => { setSelected(p); setNewMode(false); }}
                    className="w-full text-left rounded-lg border p-2.5 hover:bg-muted transition-colors" data-testid="centralino-search-result">
                    <div className="font-medium text-sm">{personName(p)}</div>
                    <div className="text-xs text-muted-foreground">{p.telefono_principale} {p.citta ? `· ${p.citta}` : ""}</div>
                  </button>
                ))}
              </div>
            )}
            {query.length >= 2 && results.length === 0 && (
              <p className="text-xs text-muted-foreground mt-2">Nessuna persona trovata.</p>
            )}
            <Button type="button" variant="outline" className="w-full mt-2 min-h-[44px]" onClick={() => setNewMode((v) => !v)} data-testid="centralino-new-person-button">
              <UserPlus className="h-4 w-4 mr-2" />{newMode ? "Annulla nuova persona" : "Nuova persona"}
            </Button>
            {newMode && (
              <div className="grid sm:grid-cols-3 gap-3 mt-3 rounded-lg border p-3 bg-slate-50">
                <div><Label htmlFor="np-cognome">Cognome *</Label><Input id="np-cognome" value={np.cognome} onChange={(e) => setP("cognome", e.target.value)} className="h-10 mt-1" data-testid="centralino-new-cognome" /></div>
                <div><Label htmlFor="np-nome">Nome</Label><Input id="np-nome" value={np.nome} onChange={(e) => setP("nome", e.target.value)} className="h-10 mt-1" data-testid="centralino-new-nome" /></div>
                <div><Label htmlFor="np-tel">Telefono *</Label><Input id="np-tel" type="tel" inputMode="tel" value={np.telefono_principale} onChange={(e) => setP("telefono_principale", e.target.value)} className="h-10 mt-1" data-testid="centralino-new-telefono" /></div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 2) Micro-form essenziale */}
      <div className={`${sectionCls} p-4`}>
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList className="h-4 w-4 text-[color:var(--brand-orange-600)]" />
          <h3 className="font-display font-semibold text-sm">Situazione in ingresso</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label htmlFor="centralino-service">Servizio interessato *</Label>
            <Select value={form.servizio_id} onValueChange={(v) => set("servizio_id", v)}>
              <SelectTrigger id="centralino-service" className="h-11 mt-1" data-testid="centralino-service-select"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
              <SelectContent className="max-h-64">{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="centralino-priorita">Priorità / urgenza percepita</Label>
            <Select value={form.priorita} onValueChange={(v) => set("priorita", v)}>
              <SelectTrigger id="centralino-priorita" className="h-11 mt-1" data-testid="centralino-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <Label htmlFor="centralino-situazione">Descrivi la situazione in ingresso *</Label>
          <DictationTextarea id="centralino-situazione" value={form.richiesta} onValueChange={(v) => set("richiesta", v)} rows={5}
            placeholder="Descrivi il più dettagliatamente possibile la richiesta e il contesto dell'utenza/contatto… oppure usa il microfono"
            className="mt-1" data-testid="centralino-situazione-textarea" />
        </div>
      </div>

      {/* 3) Sezioni di dettaglio (accordion) */}
      <Accordion type="multiple" defaultValue={["richiesta"]} className="space-y-3">
        {/* Contatto & gestione (sempre) */}
        <AccordionItem value="richiesta" className={`${sectionCls} px-4`} data-testid="centralino-section-richiesta">
          <AccordionTrigger className="min-h-[44px] hover:no-underline">
            <span className="flex items-center gap-2 font-medium text-sm"><Contact className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Contatto & gestione</span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label htmlFor="centralino-tipo">Tipo di contatto</Label>
                <Select value={form.tipo_contatto} onValueChange={(v) => set("tipo_contatto", v)}>
                  <SelectTrigger id="centralino-tipo" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{INTERACTION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label htmlFor="centralino-data-contatto">Data del contatto</Label>
                <Input id="centralino-data-contatto" type="date" max={todayISO()} value={form.data_contatto}
                  onChange={(e) => set("data_contatto", e.target.value)} className="h-11 mt-1" data-testid="centralino-data-contatto" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Quando è avvenuto il contatto (es. giorno della telefonata). Non può essere una data futura.</p>
            <div className="mt-3"><Label htmlFor="centralino-risposta">Cosa è stato risposto / fatto</Label>
              <DictationTextarea id="centralino-risposta" value={form.risposta} onValueChange={(v) => set("risposta", v)} rows={3} className="mt-1" placeholder="Scrivi o usa il microfono…" data-testid="centralino-risposta-textarea" /></div>
            <div className="flex items-center gap-2 mt-3">
              <Checkbox id="crea_pratica" checked={form.crea_pratica} onCheckedChange={(v) => set("crea_pratica", !!v)} data-testid="centralino-crea-pratica-checkbox" />
              <Label htmlFor="crea_pratica" className="cursor-pointer font-normal">Apri una pratica per questa richiesta</Label>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Checkbox id="richiamare" checked={form.richiamare} onCheckedChange={(v) => set("richiamare", !!v)} data-testid="centralino-callback-checkbox" />
              <Label htmlFor="richiamare" className="cursor-pointer font-normal">Deve essere richiamato?</Label>
            </div>
            {form.richiamare && (
              <div className="mt-2"><Label htmlFor="centralino-richiamo">Data e ora del richiamo</Label>
                <Input id="centralino-richiamo" type="datetime-local" value={form.data_richiamo} onChange={(e) => set("data_richiamo", e.target.value)} className="h-11 mt-1" /></div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Sezioni anagrafica: solo per nuova persona */}
        {newMode && !selected && (
          <>
            <AccordionItem value="anagrafica" className={`${sectionCls} px-4`} data-testid="centralino-section-anagrafica-estesa">
              <AccordionTrigger className="min-h-[44px] hover:no-underline">
                <span className="flex items-center gap-2 font-medium text-sm"><FileText className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Anagrafica (dettagli)</span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label htmlFor="np-nascita">Data di nascita</Label><Input id="np-nascita" type="date" max={todayISO()} value={np.data_nascita} onChange={(e) => setP("data_nascita", e.target.value)} className="h-10 mt-1" data-testid="centralino-np-nascita" /></div>
                  <div><Label htmlFor="np-genere">Genere</Label>
                    <Select value={np.genere} onValueChange={(v) => setP("genere", v)}>
                      <SelectTrigger id="np-genere" className="h-10 mt-1"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{GENERI.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label htmlFor="np-email">Email</Label><Input id="np-email" type="email" value={np.email} onChange={(e) => setP("email", e.target.value)} className="h-10 mt-1" data-testid="centralino-np-email" /></div>
                  <div><Label htmlFor="np-cf">Codice fiscale</Label><Input id="np-cf" value={np.codice_fiscale} onChange={(e) => setP("codice_fiscale", e.target.value.toUpperCase())} className="h-10 mt-1" maxLength={16} /></div>
                </div>
                <div className="grid sm:grid-cols-4 gap-3 mt-3">
                  <div className="sm:col-span-2"><Label htmlFor="np-indirizzo">Indirizzo</Label><Input id="np-indirizzo" value={np.indirizzo} onChange={(e) => setP("indirizzo", e.target.value)} className="h-10 mt-1" /></div>
                  <div><Label htmlFor="np-civico">N. civico</Label><Input id="np-civico" value={np.numero_civico} onChange={(e) => setP("numero_civico", e.target.value)} className="h-10 mt-1" /></div>
                  <div><Label htmlFor="np-cap">CAP</Label><Input id="np-cap" value={np.cap} onChange={(e) => setP("cap", e.target.value)} className="h-10 mt-1" maxLength={5} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <div><Label htmlFor="np-citta">Città</Label><Input id="np-citta" value={np.citta} onChange={(e) => setP("citta", e.target.value)} className="h-10 mt-1" /></div>
                  <div><Label htmlFor="np-prov">Provincia</Label><Input id="np-prov" value={np.provincia} onChange={(e) => setP("provincia", e.target.value.toUpperCase())} className="h-10 mt-1" maxLength={2} /></div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="preferenze" className={`${sectionCls} px-4`} data-testid="centralino-section-preferenze">
              <AccordionTrigger className="min-h-[44px] hover:no-underline">
                <span className="flex items-center gap-2 font-medium text-sm"><Phone className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Preferenze di contatto</span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-3"><Label htmlFor="np-provenienza">Provenienza</Label><Input id="np-provenienza" value={np.origine_contatto} onChange={(e) => setP("origine_contatto", e.target.value)} placeholder="es. passaparola, sito web, altro servizio…" className="h-10 mt-1" data-testid="centralino-new-provenienza" /></div>
                  <div><Label htmlFor="np-modalita">Modalità preferita</Label>
                    <Select value={np.modalita_contatto} onValueChange={(v) => setP("modalita_contatto", v)}>
                      <SelectTrigger id="np-modalita" className="h-10 mt-1"><SelectValue placeholder="Scegli" /></SelectTrigger>
                      <SelectContent>{MODALITA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label htmlFor="np-fascia">Fascia oraria</Label>
                    <Select value={np.fascia_oraria} onValueChange={(v) => setP("fascia_oraria", v)}>
                      <SelectTrigger id="np-fascia" className="h-10 mt-1"><SelectValue placeholder="Scegli" /></SelectTrigger>
                      <SelectContent>{FASCE.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="nucleo" className={`${sectionCls} px-4`} data-testid="centralino-section-nucleo">
              <AccordionTrigger className="min-h-[44px] hover:no-underline">
                <span className="flex items-center gap-2 font-medium text-sm"><Users className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Persone coinvolte / nucleo</span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div><Label htmlFor="np-referente">Referente familiare (nome e contatto)</Label><Input id="np-referente" value={np.referente_familiare} onChange={(e) => setP("referente_familiare", e.target.value)} className="h-10 mt-1" /></div>
                <div className="mt-3"><Label htmlFor="np-note">Note su nucleo / persone coinvolte</Label>
                  <Textarea id="np-note" value={np.note} onChange={(e) => setP("note", e.target.value)} rows={3} className="mt-1"
                    placeholder="Composizione del nucleo, persone coinvolte, informazioni utili…" /></div>
              </AccordionContent>
            </AccordionItem>
          </>
        )}
      </Accordion>

      <Button onClick={submit} disabled={saving} className="w-full h-12 min-h-[48px] text-base bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-px" data-testid="centralino-save-assign-button">
        {saving ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Check className="h-5 w-5 mr-2" />} Salva e assegna
      </Button>
    </div>
  );
};
